/** 分页与排序共享工具。 */

export interface PageQuery {
  /** 每页条数，默认 20，最大 200。 */
  limit?: number;
  /** 游标：上一页返回的 nextCursor，首页传 undefined / null。 */
  cursor?: string | null;
}

export interface Page<T> {
  items: T[];
  /** 下一页游标；为 null 表示已到末页。 */
  nextCursor: string | null;
  /** 是否还有更多数据。 */
  hasMore: boolean;
}

export const DEFAULT_PAGE_SIZE = 20;
export const MAX_PAGE_SIZE = 200;

export function normalizeLimit(limit: number | undefined): number {
  if (limit === undefined) return DEFAULT_PAGE_SIZE;
  if (!Number.isFinite(limit) || limit <= 0) return DEFAULT_PAGE_SIZE;
  return Math.min(MAX_PAGE_SIZE, Math.floor(limit));
}

/** 时间 + id 游标（用于时间序列分页，解决同毫秒排序歧义）。 */
export function encodeCursor(createdAt: number, id: string): string {
  return `${createdAt}:${id}`;
}

export function decodeCursor(cursor: string): { createdAt: number; id: string } | null {
  const sep = cursor.indexOf(':');
  if (sep <= 0) return null;
  const createdAt = Number(cursor.slice(0, sep));
  const id = cursor.slice(sep + 1);
  if (!Number.isFinite(createdAt) || id.length === 0) return null;
  return { createdAt, id };
}

/** 名称 + id 游标（用于按名称排序的列表分页）。 */
export function encodeNameCursor(name: string, id: string): string {
  return `${name}\u0000${id}`;
}

export function decodeNameCursor(cursor: string): { name: string; id: string } | null {
  const sep = cursor.indexOf('\u0000');
  if (sep < 0) return null;
  const name = cursor.slice(0, sep);
  const id = cursor.slice(sep + 1);
  if (id.length === 0) return null;
  return { name, id };
}

/** 中文友好的字符串比较（拼音/笔画排序交由 Intl 处理，缺失时退化为码点比较）。 */
export function compareZh(a: string, b: string): number {
  try {
    return a.localeCompare(b, 'zh-Hans-CN');
  } catch {
    return a < b ? -1 : a > b ? 1 : 0;
  }
}

/**
 * 名称 + id 的稳定排序比较器。
 * 返回 0 表示相等；列表分页游标过滤依赖它判断「是否在游标之后」。
 */
export function compareNameThenId(
  a: { name: string; id: string },
  b: { name: string; id: string },
): number {
  return compareZh(a.name, b.name) || compareZh(a.id, b.id);
}